import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const LOGO_URL = '/logo.jpg';

/**
 * Shared layout for all legal pages (/terms, /privacy, /community-guidelines, /safety).
 * Public route — works without auth.
 */
export default function LegalLayout({ title, badge, badgeIcon: BadgeIcon, badgeColor = '#C8553D', lastUpdated, version, intro, introBorderColor = '#C8553D', children }) {
  const navigate = useNavigate();

  // Smart back: if there's history, go back; otherwise go to the most likely origin (signup → /app/signup, else /).
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // Opened in a fresh tab (target="_blank") — fall back to landing
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFCF9]" data-testid={`legal-page-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      {/* Top bar */}
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-[#F4F1DE]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button
            onClick={handleBack}
            data-testid="legal-back-button"
            className="w-10 h-10 rounded-full hover:bg-[#F4F1DE] flex items-center justify-center transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5 text-[#2B2D42]" />
          </button>
          <Link to="/" className="flex items-center gap-2" data-testid="legal-home-link">
            <div className="w-8 h-8 rounded-lg bg-white border border-[#F4F1DE] flex items-center justify-center overflow-hidden">
              <img src={LOGO_URL} alt="AfroConnect" className="w-7 h-7 object-contain" />
            </div>
            <span className="font-serif text-lg text-[#2B2D42]" style={{ fontFamily: 'Playfair Display, serif' }}>
              AfroConnect
            </span>
          </Link>
          <div className="w-10" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-20">
        {/* Title */}
        <h1
          className="text-3xl sm:text-4xl font-serif font-bold text-[#2B2D42] mb-3"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          {title}
        </h1>

        {/* Badge */}
        {badge && (
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4"
            style={{ backgroundColor: `${badgeColor}15`, color: badgeColor }}
          >
            {BadgeIcon && <BadgeIcon className="w-4 h-4" />}
            {badge}
          </div>
        )}

        {/* Meta */}
        <div className="text-sm text-[#6D6875] mb-2">
          <span className="font-semibold">Last Updated:</span> {lastUpdated}
        </div>
        {version && (
          <div className="text-xs text-[#B5B5B5] mb-6">Version {version}</div>
        )}

        {/* Intro card */}
        {intro && (
          <div
            className="bg-white border-l-4 rounded-r-2xl p-5 mb-8 shadow-sm"
            style={{ borderLeftColor: introBorderColor }}
            data-testid="legal-intro"
          >
            <p className="text-[15px] text-[#2B2D42] leading-relaxed">{intro}</p>
          </div>
        )}

        {/* Body */}
        <div className="space-y-6">{children}</div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-[#F4F1DE] text-center">
          <p className="text-sm text-[#6D6875]">
            Questions?{' '}
            <a
              href="mailto:support@afroconnect.ca"
              className="font-bold text-[#C8553D] hover:text-[#9E3A26] underline"
              data-testid="legal-contact-email"
            >
              support@afroconnect.ca
            </a>
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-[#B5B5B5]">
            <Link to="/terms" className="hover:text-[#C8553D]">Terms</Link>
            <span>·</span>
            <Link to="/privacy" className="hover:text-[#C8553D]">Privacy</Link>
            <span>·</span>
            <Link to="/community-guidelines" className="hover:text-[#C8553D]">Community Guidelines</Link>
            <span>·</span>
            <Link to="/safety" className="hover:text-[#C8553D]">Safety Tips</Link>
          </div>
          <p className="mt-4 text-xs text-[#B5B5B5]">© {new Date().getFullYear()} AfroConnect. Where Africans Meet in Canada.</p>
        </div>
      </main>
    </div>
  );
}

/* ───────────────── Reusable section primitives ───────────────── */

export function LegalSection({ number, title, icon: Icon, iconColor = '#C8553D', children }) {
  return (
    <section className="bg-white border border-[#F4F1DE] rounded-2xl p-5 sm:p-6" data-testid={`legal-section-${number || title.toLowerCase().replace(/\s+/g, '-').slice(0, 30)}`}>
      <div className="flex items-start gap-3 mb-3">
        {Icon && (
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${iconColor}15` }}
          >
            <Icon className="w-4 h-4" style={{ color: iconColor }} />
          </div>
        )}
        <h2 className="text-lg font-bold text-[#2B2D42] leading-snug pt-1.5">
          {number && <span className="text-[#B5B5B5] mr-1.5">{number}.</span>}
          {title}
        </h2>
      </div>
      <div className="text-sm text-[#3A3D52] leading-relaxed space-y-2 pl-12 [&_strong]:font-bold [&_strong]:text-[#2B2D42] [&_a]:text-[#C8553D] [&_a]:font-bold [&_a]:underline">
        {children}
      </div>
    </section>
  );
}

export function LegalSubsection({ title, children }) {
  return (
    <div className="border-l-2 border-[#FFE5DD] pl-4 my-3">
      <h3 className="text-sm font-bold text-[#2B2D42] mb-1.5">{title}</h3>
      <div className="text-sm text-[#6D6875] leading-relaxed">{children}</div>
    </div>
  );
}

export function BulletList({ items }) {
  return (
    <ul className="space-y-1.5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2">
          <span className="text-[#C8553D] mt-1.5 flex-shrink-0">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}
